export type LandingPath = "/dashboard" | "/events";

/** Longest a newcomer with no memberships waits for the roster claim. */
export const CLAIM_WAIT_MS = 10_000;

/**
 * How old a SUCCEEDED request without `rosterClaimedAt` must be before it is
 * taken as settled. Such a request is an account provisioned before the mark
 * existed, and nothing backfills it; a fresh one is a claim still running.
 */
export const STALE_SUCCESS_MS = 5 * 60_000;

/**
 * How old a request still at REQUESTED must be before it is taken as
 * settled. Provisioning finishes in seconds; one stuck this long is not going
 * to finish while the person waits, and without this cut-off somebody in no
 * event would sit on the loading screen for CLAIM_WAIT_MS on every visit.
 */
export const STALE_REQUEST_MS = STALE_SUCCESS_MS;

/** The fields of `user_provisioning_requests/{uid}` the landing reads. */
export type ProvisioningRequest = {
  status?: unknown;
  rosterClaimedAt?: unknown;
  /** Milliseconds since the epoch, or null when absent. */
  completedAtMs: number | null;
  /** Milliseconds since the epoch, or null/absent when not (yet) known. */
  requestedAtMs?: number | null;
};

/**
 * Whether the roster claim for this account has had its turn, so an empty
 * membership list can be believed.
 *
 * `request` is null when there is no provisioning request. The client files
 * one only for an account with no profile, so a missing request beside an
 * existing profile means no claim is coming (an account provisioned some
 * other way), while a missing request with no profile is a first sign-in
 * whose request has not been written yet.
 */
export function claimSettled(
  request: ProvisioningRequest | null,
  profileExists: boolean,
  nowMs: number,
): boolean {
  if (request === null) return profileExists;
  if (request.status === "FAILED") return true;
  if (request.rosterClaimedAt != null) return true;
  if (request.status === "REQUESTED") {
    return (
      request.requestedAtMs != null &&
      nowMs - request.requestedAtMs > STALE_REQUEST_MS
    );
  }
  return (
    request.status === "SUCCEEDED" &&
    request.completedAtMs !== null &&
    nowMs - request.completedAtMs > STALE_SUCCESS_MS
  );
}

export type LandingSources = {
  /** How many memberships the person has. Throws when the read fails. */
  membershipCount: () => Promise<number>;
  request: () => Promise<ProvisioningRequest | null>;
  profileExists: () => Promise<boolean>;
  /**
   * Resolves once `settled` holds for the request, or after `capMs`, or when
   * the request cannot be watched, or when `signal` aborts. Never rejects.
   * Every way out stops the listener and the timer.
   */
  waitForRequest: (
    settled: (request: ProvisioningRequest | null) => boolean,
    capMs: number,
    signal?: AbortSignal,
  ) => Promise<void>;
  now: () => number;
};

/**
 * Where "home" is for a signed-in person.
 *
 * Somebody in no event has an empty My Events, and on a first sign-in that
 * reads like something went wrong, so they start on All Events. Everyone else
 * starts on My Events.
 *
 * A person with memberships costs one query. Only an empty answer reads the
 * provisioning request, because on a first sign-in the roster claim that
 * links an organiser's roster rows to the new account runs after the
 * profile is written, and the membership query usually answers first. When
 * the claim has not had its turn, this waits for it (at most CLAIM_WAIT_MS)
 * and asks once more.
 *
 * A failed membership read goes to My Events, which shows the error.
 *
 * The profile is read before the request. The request is written before the
 * profile, so a profile seen first means any request already exists; read the
 * other way round, a first sign-in could see "no request" and then, a moment
 * later, the fresh profile, and skip a claim that is about to run.
 *
 * `signal` aborts the wait when the caller no longer needs the answer.
 */
export async function decideLanding(
  sources: LandingSources,
  signal?: AbortSignal,
): Promise<LandingPath> {
  try {
    if ((await sources.membershipCount()) > 0) return "/dashboard";

    let request: ProvisioningRequest | null;
    let profileExists: boolean;
    try {
      profileExists = await sources.profileExists();
      request = await sources.request();
    } catch {
      // Unreadable: nothing to wait on, so the empty answer stands.
      return "/events";
    }
    if (claimSettled(request, profileExists, sources.now())) return "/events";

    await sources.waitForRequest(
      (next) => claimSettled(next, false, sources.now()),
      CLAIM_WAIT_MS,
      signal,
    );
    // Nobody is listening for the answer any more; skip the second query.
    if (signal?.aborted) return "/events";
    return (await sources.membershipCount()) > 0 ? "/dashboard" : "/events";
  } catch {
    return "/dashboard";
  }
}
